import { Button, ServiceError, TOTPCodeInput } from "@shapr3d/frontend-lib";
import { Controller, useForm } from "react-hook-form";

import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import { TOTPErrorCode, useDeleteTOTP } from "../../hooks/useTwoFactor";
import { Modal } from "../../lib/modal";

import style from "./TwoFactorDisableModal.module.css";

export type TwoFactorDisableModalProps = {
  open: boolean;
  onClose: () => void;
};

type FormData = {
  code: string;
};

export default function TwoFactorDisableModal({ open, onClose }: TwoFactorDisableModalProps) {
  const { t, m } = useTranslation();
  const tracker = useTracker();
  const {
    control,
    watch,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      code: "",
    },
  });

  const { mutateAsync: mutateDeleteTOTP, isPending } = useDeleteTOTP();
  const codeValue = watch("code");

  const onSubmit = async (data: FormData) => {
    try {
      await mutateDeleteTOTP({ code: data.code });
      onClose();
    } catch (error) {
      let message = t(m.TwoFactorAuth.Disable.ErrorTryAgain);
      if (error instanceof ServiceError) {
        const errorCode = error.serviceErrorResponse.code;
        if (errorCode === TOTPErrorCode.InvalidCode) {
          message = t(m.TwoFactorAuth.Disable.InvalidCode);
        }
      }
      setError("code", { message });
    }
  };

  return (
    <Modal
      open={open}
      telemetryName="two_factor_disable"
      onClose={onClose}
      size="medium"
      title={t(m.TwoFactorAuth.Disable.Title)}
      footer={
        <div className={style.footer}>
          <Button
            variant="red"
            onClick={() => {
              handleSubmit(onSubmit)();
            }}
            disabled={isPending || codeValue.length !== 6}
            formNoValidate
          >
            {t(m.TwoFactorAuth.Disable.DisableAnyway)}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              tracker.trackTwoFactorDisableAct({ action: "keep_enabled" });
              onClose();
            }}
          >
            {t(m.TwoFactorAuth.Disable.Keep2FAEnabled)}
          </Button>
        </div>
      }
    >
      <div className={style.container}>
        <div className={style.content}>
          <div className={style.description}>
            {t(m.TwoFactorAuth.Disable.Description)}
            <div className={style.instruction}>{t(m.TwoFactorAuth.Disable.Instruction)}</div>
            <Controller
              name="code"
              control={control}
              render={({ field }) => (
                <TOTPCodeInput
                  onChange={field.onChange}
                  onComplete={(value) => {
                    field.onChange(value);
                  }}
                  error={Boolean(errors.code)}
                  errorMessage={errors.code?.message}
                  disabled={isPending}
                  autoFocus
                />
              )}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
